import { ReactNode } from "react"

import { useTrans } from "@/i18n"

import { Card } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { ButtonLink } from "@/components/Button"

import { FiAlertTriangle, FiChevronRight } from "@/lib/icons"

import { Routes } from "@/constants/routes"
import { contact } from "@/constants/constants"

import { useMangopayConsentStatus } from "@/context/user"

type GateState = "not_ready" | "ready" | "loading" | "not_enrolled"

function useGateState(): GateState {
	const { isLoading, hasMangopayUser, allGranted, hasScaData } =
		useMangopayConsentStatus()
	if (isLoading) return "loading"
	// Users without a Mangopay user (non-investor roles landing on auth-only
	// routes like /settings/profile and /settings/bank-details, or freshly
	// signed-up investors not yet provisioned) have nothing to consent to —
	// render the blocking provider-not-ready modal to contact support
	if (!hasMangopayUser || !hasScaData) {
		return "not_ready"
	}
	if (!allGranted) return "not_enrolled"
	return "ready"
}

export function useMangopayGateActive(): boolean {
	const state = useGateState()
	return state !== "ready" && state !== "loading"
}

export function MangopayGate({ children }: { children: ReactNode }) {
	const state = useGateState()

	if (state === "ready" || state === "loading") {
		return <>{children}</>
	}

	if (state === "not_ready") {
		return (
			<div className="relative min-h-[60vh]">
				<div inert>{children}</div>
				<MangopayNotReadyOverlay />
			</div>
		)
	}

	return (
		<div className="relative min-h-[60vh]">
			<div inert>{children}</div>
			<NotEnrolledOverlay />
		</div>
	)
}

export function MangopayNotReadyOverlay() {
	const t = useTrans("investor")
	const telHref = `tel:${contact.phoneNumber.replace(/\s+/g, "")}`

	return (
		<div
			className="absolute inset-0 z-10 flex items-center justify-center bg-gray-600/50 p-5 text-center"
			data-testid="MangopayGate-not-ready"
		>
			<Card className="mx-auto max-w-md">
				<div className="space-y-4 text-center">
					<Heading
						as="h2"
						styleAs="h5"
						className="flex justify-center gap-2"
					>
						<FiAlertTriangle
							aria-hidden="true"
							className="size-5 shrink-0 text-yellow-500"
						/>
						{t("investor:mangopay.not_ready.title")}
					</Heading>
					<p className="text-gray-600">
						{t("investor:mangopay.not_ready.body")}
					</p>
					<div className="space-y-1 text-sm">
						<p className="text-gray-600">
							{t("investor:mangopay.not_ready.contact_intro")}
						</p>
						<p>
							<a
								className="text-secondary-700 font-medium hover:underline"
								href={`mailto:${contact.emailAddress}`}
							>
								{contact.emailAddress}
							</a>
						</p>
						<p>
							<a
								className="text-secondary-700 font-medium hover:underline"
								href={telHref}
							>
								{contact.phoneNumber}
							</a>
						</p>
					</div>
				</div>
			</Card>
		</div>
	)
}

function NotEnrolledOverlay() {
	const t = useTrans("investor")

	return (
		<div
			className="absolute inset-0 z-10 flex items-center justify-center bg-gray-600/50 p-5 text-center"
			data-testid="MangopayGate-not-enrolled"
		>
			<Card className="mx-auto max-w-md">
				<div className="space-y-4 text-center">
					<Heading
						as="h2"
						styleAs="h5"
						className="flex justify-center gap-2"
					>
						<FiAlertTriangle
							aria-hidden="true"
							className="size-5 shrink-0 text-yellow-500"
						/>
						{t("investor:mangopay.not_enrolled.title")}
					</Heading>
					<p className="text-gray-600">
						{t("investor:mangopay.not_enrolled.body")}
					</p>
					<div>
						<ButtonLink to={Routes.InvestorPayments}>
							{t("investor:mangopay.not_enrolled.action")}
							<FiChevronRight className="ml-2" />
						</ButtonLink>
					</div>
				</div>
			</Card>
		</div>
	)
}
